import { useState, useCallback } from 'react'
import { useAuth } from '@/modules/auth/hooks/useAuth'
import { RoutesApi, TripRoute, UpdateTripRouteAssociationDto, TripRouteScheduleRoute } from '@/shared/api/routes'

export function useRouteAssociations() {
  const { getToken } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const routesApi = useCallback(() => new RoutesApi(getToken), [getToken])

  const associateRouteToTrip = useCallback(
    async (
      routeId: string,
      tripId: string,
      association?: {
        notes?: string
        planned_date?: string | null
        planned_start_time?: string | null
        planned_end_time?: string | null
        planned_order?: number | null
      }
    ): Promise<TripRoute> => {
    try {
      setLoading(true)
      setError(null)
      const api = routesApi()
      const result = await api.associateRouteToTrip(routeId, tripId, association)
      return result
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to associate route to trip')
      throw err
    } finally {
      setLoading(false)
    }
  },
  [routesApi])

  const dissociateRouteFromTrip = useCallback(async (routeId: string, tripId: string) => {
    try {
      setLoading(true)
      setError(null)
      const api = routesApi()
      await api.dissociateRouteFromTrip(routeId, tripId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to dissociate route from trip')
      throw err
    } finally {
      setLoading(false)
    }
  }, [routesApi])

  const getTripsByRoute = useCallback(async (routeId: string) => {
    // Skip if routeId is invalid
    if (!routeId || routeId === 'undefined' || routeId === 'null') {
      return []
    }

    try {
      setLoading(true)
      setError(null)
      const api = routesApi()
      return await api.getTripsByRoute(routeId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch trips for route')
      throw err
    } finally {
      setLoading(false)
    }
  }, [routesApi])

  const updateTripRouteAssociation = useCallback(
    async (associationId: string, updates: UpdateTripRouteAssociationDto): Promise<TripRouteScheduleRoute> => {
      try {
        setLoading(true)
        setError(null)
        const api = routesApi()
        return await api.updateTripRouteAssociation(associationId, updates)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update route schedule')
        throw err
      } finally {
        setLoading(false)
      }
    },
    [routesApi]
  )

  return {
    loading,
    error,
    associateRouteToTrip,
    dissociateRouteFromTrip,
    getTripsByRoute,
    updateTripRouteAssociation
  }
}
